import {
  Component,
  ElementRef,
  HostBinding,
  HostListener,
  Input,
  OnDestroy,
  ViewChild,
} from "@angular/core";

import { Router } from "@angular/router";
import { TranslateModule } from "@ngx-translate/core";
import { Subscription } from "rxjs";

import { AuthService } from "../../../login/auth.service";
import { TokenStorageService } from "../../../login/token-storage.service";
import {
  LinkedProfile,
  ProfilesService,
  TYPE_LABELS,
} from "../../../login/profile-picker/profiles.service";
import { global_user } from "../../../global/global";
import { Facet, facetMark } from "../../../utils/facet-mark";
import { hopFailure } from "./hop-failure";

interface HopperRow {
  tenantId: number;
  name: string;
  type: string | null;
  current: boolean;
  facets: Facet[];
}

/**
 * C31: a superuser's header button for the open business. It opens a
 * searchable list of the linked businesses; picking one hops straight there
 * (AuthService.hopToProfile), the footer leads to the business portal.
 */
@Component({
  selector: "app-business-hopper",
  imports: [TranslateModule],
  templateUrl: "./business-hopper.component.html",
  styleUrls: ["./business-hopper.component.scss"],
})
export class BusinessHopperComponent implements OnDestroy {
  @Input() @HostBinding("class.is-phone") mobile = false;
  @ViewChild("trigger") private trigger?: ElementRef<HTMLButtonElement>;

  /** Desktop: typing starts at once. */
  @ViewChild("search") set searchField(
    ref: ElementRef<HTMLInputElement> | undefined
  ) {
    if (ref && !this.mobile) ref.nativeElement.focus();
  }

  open = false;
  query = "";
  rows: HopperRow[] = [];
  /** Tenant id of the highlighted row; never the open business. */
  active: number | null = null;
  loading = false;
  loadFailed = false;
  /** Tenant being opened; the list is locked meanwhile. */
  hopping: number | null = null;
  note: { key: string; params?: Record<string, string> } | null = null;

  private profiles: LinkedProfile[] = [];
  private listing?: Subscription;

  constructor(
    private host: ElementRef<HTMLElement>,
    private auth: AuthService,
    private profilesApi: ProfilesService,
    private tokenStorage: TokenStorageService,
    private router: Router
  ) {}

  get tenantId(): number {
    return this.tokenStorage.tenantId() ?? 0;
  }

  get companyName(): string {
    return global_user.user.company_name || "";
  }

  get facets(): Facet[] {
    return facetMark(this.tenantId);
  }

  get edge(): string {
    return `linear-gradient(90deg, ${this.facets
      .map((f) => f.fill)
      .join(", ")})`;
  }

  toggle(): void {
    if (this.open) this.close();
    else this.show();
  }

  close(focusTrigger = false): void {
    if (this.hopping !== null) return;
    this.open = false;
    this.listing?.unsubscribe();
    if (focusTrigger) this.trigger?.nativeElement.focus();
  }

  filter(query: string): void {
    this.query = query;
    this.render();
    this.active = this.rows.find((row) => !row.current)?.tenantId ?? null;
  }

  onSearchKey(event: KeyboardEvent): void {
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      this.move(event.key === "ArrowDown" ? 1 : -1);
    } else if (event.key === "Enter") {
      event.preventDefault();
      const row = this.rows.find((r) => r.tenantId === this.active);
      if (row) this.hop(row);
    }
  }

  onPanelKey(event: KeyboardEvent): void {
    if (event.key === "Escape") {
      event.preventDefault();
      this.close(true);
    }
  }

  @HostListener("document:click", ["$event.target"])
  onDocumentClick(target: EventTarget | null): void {
    if (
      this.open &&
      target instanceof Node &&
      !this.host.nativeElement.contains(target)
    )
      this.close();
  }

  async hop(row: HopperRow): Promise<void> {
    if (row.current || this.hopping !== null) return;
    this.hopping = row.tenantId;
    this.active = row.tenantId;
    this.note = null;
    try {
      await this.auth.hopToProfile(row.tenantId);
    } catch (error) {
      this.hopping = null;
      switch (hopFailure(error)) {
        case "unlinked":
          this.note = {
            key: "PROFILES.HOP.UNLINKED",
            params: { name: row.name },
          };
          this.load();
          break;
        case "signed-out":
          this.open = false;
          this.auth.logout();
          this.router.navigate(["/login"]);
          break;
        default:
          this.note = { key: "PROFILES.HOP.FAILED" };
      }
    }
  }

  openPortal(): void {
    this.auth.switchProfile();
  }

  byId(_: number, row: HopperRow): number {
    return row.tenantId;
  }

  ngOnDestroy(): void {
    this.listing?.unsubscribe();
  }

  private show(): void {
    this.open = true;
    this.query = "";
    this.note = null;
    this.profiles = [];
    this.loading = true;
    this.render();
    this.active = null;
    this.load();
  }

  /** A refetch keeps the rows on screen; only the first load of an open shows "Loading". */
  private load(): void {
    this.loadFailed = false;
    this.listing?.unsubscribe();
    this.listing = this.profilesApi.list().subscribe({
      next: (profiles) => {
        this.profiles = profiles;
        this.loading = false;
        this.render();
        if (
          !this.rows.some((row) => row.tenantId === this.active && !row.current)
        ) {
          this.active = this.rows.find((row) => !row.current)?.tenantId ?? null;
        }
      },
      error: () => {
        this.loading = false;
        this.loadFailed = true;
      },
    });
  }

  /** Open business first — straight from the session, so it shows before the list arrives. */
  private render(): void {
    const here = this.tenantId;
    const listed = this.profiles.find((p) => p.tenantId === here);
    const current: HopperRow = {
      tenantId: here,
      name: listed?.companyName || this.companyName,
      type: listed ? TYPE_LABELS[listed.companyType] ?? null : null,
      current: true,
      facets: facetMark(here),
    };
    const others = this.profiles
      .filter((p) => p.tenantId !== here)
      .map((p) => ({
        tenantId: p.tenantId,
        name: p.companyName,
        type: TYPE_LABELS[p.companyType] ?? null,
        current: false,
        facets: facetMark(p.tenantId),
      }));
    const needle = this.query.trim().toLocaleLowerCase("tr");
    this.rows = [current, ...others].filter(
      (row) => !needle || row.name.toLocaleLowerCase("tr").includes(needle)
    );
  }

  /** ↑↓ step over the businesses that can be opened; they stop at either end. */
  private move(step: 1 | -1): void {
    const ids = this.rows
      .filter((row) => !row.current)
      .map((row) => row.tenantId);
    if (!ids.length) return;
    const at = this.active === null ? -1 : ids.indexOf(this.active);
    this.active = ids[Math.min(ids.length - 1, Math.max(0, at + step))];
    document
      .getElementById(`hopper-row-${this.active}`)
      ?.scrollIntoView({ block: "nearest" });
  }
}
